import {
  computed,
  IObservableArray,
  makeAutoObservable,
  observable,
} from "mobx";
import { ModuleStore } from "./ModuleStore";
import { ModuleResourceRowDTO } from "../../ProfileWindow/AdminWindows/ModuleEditWindow/DTO/ModuleResourceRowDTO";
import { ResourceTypeEnum } from "../../Enums/ResourceTypeEnum";
import { ModuleResourceRequestImpl } from "../../DTO/ClientRequest/ModuleRequest";
import {
  ModuleResourceResponseApi,
  ModuleResourceWithScenarioOrMaterialResponseApi,
} from "../../DTO/ServerResponseType/Module/ModuleResource";
import { CheckArgs } from "../../utils/checks/CheckArgsWithoutThrow";

export class ModuleResourcesStore {
  root: ModuleStore;

  sortModuleResourcesWithoutScenarioAndMaterials: IObservableArray<ModuleResourceRowDTO> =
    observable.array([]);
  scenarios: IObservableArray<ModuleResourceWithScenarioOrMaterialResponseApi> =
    observable.array([]);
  materials: IObservableArray<ModuleResourceWithScenarioOrMaterialResponseApi> =
    observable.array([]);

  constructor(root: ModuleStore) {
    this.root = root;
    makeAutoObservable(this);
  }

  sortByOrder = (array: { order: number }[]) => {
    array.sort((a, b) => {
      return a.order - b.order;
    });
  };

  getModuleResourcesByCondition = (
    moduleResources: ModuleResourceResponseApi[],
    condition: (moduleResource: ModuleResourceResponseApi) => boolean
  ) => {
    return Array.from(
      moduleResources.filter((moduleResource) => condition(moduleResource)),
      (moduleResource) => {
        let row = new ModuleResourceRowDTO();
        row.setFromModuleResourceResponse(moduleResource);
        return row;
      }
    );
  };

  setModuleResources = (moduleResources: ModuleResourceResponseApi[]) => {
    this.sortModuleResourcesWithoutScenarioAndMaterials.replace(
      this.getModuleResourcesByCondition(
        moduleResources,
        (moduleResource) =>
          moduleResource.resource?.resourceTypeId !==
            ResourceTypeEnum.Scenario &&
          moduleResource.resource?.resourceTypeId !==
            ResourceTypeEnum.MaterialForStudent
      )
    );

    this.sortByOrder(this.sortModuleResourcesWithoutScenarioAndMaterials);

    this.scenarios.replace(
      moduleResources.filter(
        (moduleResource) =>
          moduleResource.resource?.resourceTypeId === ResourceTypeEnum.Scenario
      ) as ModuleResourceWithScenarioOrMaterialResponseApi[]
    );

    this.materials.replace(
      moduleResources.filter(
        (moduleResource) =>
          moduleResource.resource?.resourceTypeId ===
          ResourceTypeEnum.MaterialForStudent
      ) as ModuleResourceWithScenarioOrMaterialResponseApi[]
    );
  };

  onChangeOrder = (
    moduleResourceData: ModuleResourceRowDTO,
    newOrder: number
  ) => {
    if (
      !this.sortModuleResourcesWithoutScenarioAndMaterials.includes(
        moduleResourceData
      )
    ) {
      console.log("Ресурс модуля не найден: " + moduleResourceData.name);
      return;
    }

    let temp1 =
      this.sortModuleResourcesWithoutScenarioAndMaterials[
        moduleResourceData.order - 1
      ].copy();

    let temp2 =
      this.sortModuleResourcesWithoutScenarioAndMaterials[newOrder - 1].copy();
    // reordering

    temp1.onChangeOrder(newOrder);
    temp2.onChangeOrder(moduleResourceData.order);

    // swap
    this.sortModuleResourcesWithoutScenarioAndMaterials[
      moduleResourceData.order - 1
    ] = temp2;

    this.sortModuleResourcesWithoutScenarioAndMaterials[newOrder - 1] = temp1;
  };

  onChangeRequired = (moduleResourceData: ModuleResourceRowDTO) => {
    if (
      !this.sortModuleResourcesWithoutScenarioAndMaterials.includes(
        moduleResourceData
      )
    ) {
      console.log("Ресурс модуля не найден: " + moduleResourceData.name);
      return;
    }

    this.sortModuleResourcesWithoutScenarioAndMaterials[
      moduleResourceData.order - 1
    ].onChangeRequired(!moduleResourceData.required);
  };

  onChangeModuleResources = (
    moduleResources: IObservableArray<ModuleResourceRequestImpl>,
    ids: string[]
  ): void => {
    let delResources = moduleResources.filter(
      (resource) => !ids.includes(resource.resourceId!)
    );

    let addResourcesIds = ids.filter(
      (id) =>
        moduleResources.find((resource) => resource.resourceId === id) ===
        undefined
    );

    moduleResources.replace(
      moduleResources.filter((resource) => !delResources.includes(resource))
    );

    addResourcesIds.forEach((id) => {
      moduleResources.push({ resourceId: id });
    });
  };

  onChangeModuleScenarios = (ids: string[]): void => {
    this.onChangeModuleResources(this.scenarios, ids);
  };

  onChangeModuleMaterials = (ids: string[]): void => {
    this.onChangeModuleResources(this.materials, ids);
  };

  @computed get moduleResources(): (
    | ModuleResourceWithScenarioOrMaterialResponseApi
    | ModuleResourceRowDTO
  )[] {
    return [
      ...this.sortModuleResourcesWithoutScenarioAndMaterials,
      ...this.scenarios,
      ...this.materials,
    ];
  }

  @computed get scenariosIds(): string[] {
    return this.scenarios.map((scenatio) => scenatio.resource.resourceId);
  }

  @computed get materialsIds(): string[] {
    return this.materials.map((material) => material.resource?.resourceId);
  }

  addResource = (moduleResourceData: ModuleResourceRowDTO) => {
    if (
      CheckArgs.CheckNullOrUndefinedOrEmptyString(
        moduleResourceData.resourceId ?? moduleResourceData.taskId
      )
    ) {
      throw Error("Выберите ресурс");
    }

    // reordering
    this.sortModuleResourcesWithoutScenarioAndMaterials.forEach(
      (moduleResource, index) => {
        let order = index + 1;

        if (moduleResourceData.order <= order) order++;

        moduleResource.onChangeOrder(order);
      }
    );

    // add
    this.sortModuleResourcesWithoutScenarioAndMaterials.push(
      moduleResourceData.copy()
    );

    this.sortByOrder(this.sortModuleResourcesWithoutScenarioAndMaterials);
  };

  deleteModuleResource = (moduleResourceData: ModuleResourceRowDTO) => {
    let iModuleResource =
      this.sortModuleResourcesWithoutScenarioAndMaterials.indexOf(
        moduleResourceData
      );

    if (iModuleResource === -1) {
      console.log("Ресурс модуля не найден: " + moduleResourceData.name);
      return;
    }

    //delete
    this.sortModuleResourcesWithoutScenarioAndMaterials.splice(
      iModuleResource,
      1
    );

    // reordering
    this.sortModuleResourcesWithoutScenarioAndMaterials.forEach(
      (moduleResource, index) => {
        let order = index + 1;
        moduleResource.onChangeOrder(order);
      }
    );
  };

  // computed

  @computed get moduleRequiredResources(): number {
    let moduleRequiredResources =
      this.sortModuleResourcesWithoutScenarioAndMaterials.reduce(
        (count: number, resource: ModuleResourceRowDTO) => {
          return count + (resource.required ? 1 : 0);
        },
        0
      );
    return moduleRequiredResources;
  }

  @computed get moduleMaxCost(): number {
    let moduleMaxCost =
      this.sortModuleResourcesWithoutScenarioAndMaterials.reduce(
        (sum: number, resource: ModuleResourceRowDTO) => {
          return sum + resource.cost;
        },
        0
      );
    return moduleMaxCost;
  }
}
